import { ThreeDotMenu } from '@mm/components';
import subscriptionCRUDActions from '@mm/state/actions/SubscriptionCRUDActions';
import {
  getPlanInfo,
  mapPlanAllotmentOptions,
} from '@mm/state/helpers/billing-info-helpers';
import {
  PROMOTION_SUBSCRIPTION_PLAN_CODE,
  SubscriptionTabDefinition,
  TEAM_TRIAL_SUBSCRIPTION_PLAN_CODE,
} from '@mm/state/helpers/billing-subscription-tab-helpers';
import {
  EnhancedBillingPage,
  PageBillingType,
} from '@mm/state/interfaces/connected-page';
import {
  PlansAllotments,
  SubscriptionResponse,
  EnhancedPlan,
} from '@mm/state/interfaces/subscription';
import PromotionModel from '@mm/state/model/PromotionModel';
import {
  OpenBillingModalParams,
  useModalActions,
} from '@mm/state/modules/modalsState/actions';
import {
  enhancedPlansSelector,
  getCurrentSubscription,
  getPlansUserSeats,
} from '@mm/state/selectors/subscriptionSelectors';
import { twilioNumbersSelector } from '@mm/state/selectors/viewSelectors';
import { StoreState } from '@mm/state/store-interfaces';
import { abbreviateNumber, centsToDollars } from '@mm/utils';
import noImage from 'assets/icons/contact_noimage.svg';
import { ReactComponent as ExclamationIcon } from 'assets/red-info.svg';
import classNames from 'classnames';
import { capitalize, isNumber } from 'lodash';
import moment from 'moment/moment';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { PrimaryButton } from '../../../../../../../common/buttons/PrimaryButton/PrimaryButton';
import { ImageWithFallback } from '../../../../../../../common/components/ImageWithFallback/ImageWithFallback';
import PrimarySelect, {
  DropdownOption,
} from '../../../../../../../common/components/PrimarySelect/PrimarySelect';
import { PrimaryToolTip } from '../../../../../../../common/components/PrimaryToolTip/PrimaryToolTip';
import { PlanBadge } from '../../../../../../../common/components/ProBadge';
import {
  getUsedAndTotal,
  UsagePercentageBar,
} from '../../../../../../../common/components/UsagePercentageBar/UsagePercentageBar';
import { useOpenConditionalDowngradeModal } from '../../../../../../../common/hooks/use-open-conditional-downgrade-modal';
import { useFreeTrialUpgradeModal } from '../../../../../../../common/modals/FreeTrialUpgradeModal/use-free-trial-upgrade-modal';
import OveragesManagement from '../OveragesManagement/OveragesManagement';
import { SeatsArea } from '../SeatsArea/SeatsArea';
import { SubscriptionMetaField } from '../SubscriptionMetaField/SubscriptionMetaField';
import BillingCardRightSection from './BillingCardRightSection/BillingCardRightSection';
import './BillingPageCard.pcss';
import PromoCodeArea from './PromoCodeArea/PromoCodeArea';

type Props = {
  page: EnhancedBillingPage;
  openRemoveFromTeamModal: () => void;
  promotions: PromotionModel[];
  accountDetails?: SubscriptionResponse;
  isCompanyAdmin: boolean;
  planTab: SubscriptionTabDefinition;
  monthlyPlansAllotments: PlansAllotments;
  annualPlansAllotments: PlansAllotments;
  openBillingModal: ({
    level,
    callbackFunction,
    subscription,
    facebookPage,
    plan,
    stayOnPage,
  }: Partial<OpenBillingModalParams>) => void;
  loadPricesForNextLevel: (subscriptionId: number, level: number) => void;
};

const getSubscriptionIsCanceled = (page: EnhancedBillingPage) => {
  return (
    (page.subscription && page.subscription.state === 'canceled') ||
    (page.subscription && page.subscription.downgradePending)
  );
};

const getLastCardNumbers = (accountDetails?: SubscriptionResponse) => {
  return accountDetails && accountDetails.account
    ? accountDetails.account.lastCardNumbers
    : 'XXXX';
};

const getBillingPlanForTab = (
  planTab: SubscriptionTabDefinition,
): PageBillingType | undefined => {
  if (planTab.plan) {
    return planTab.plan.pageBillingType;
  }
  if (planTab.planCode === PROMOTION_SUBSCRIPTION_PLAN_CODE) {
    return PageBillingType.Promotion;
  }
  if (planTab.planCode === TEAM_TRIAL_SUBSCRIPTION_PLAN_CODE) {
    return PageBillingType.TrialTeam;
  }
};

export const planCanManageOverages = (
  limit: number | null | undefined,
  planInfo: ReturnType<typeof getPlanInfo>,
) =>
  !(
    limit === null ||
    limit === undefined ||
    planInfo.isFreePlan ||
    planInfo.isAnyTeamPlan ||
    planInfo.isInstachamp
  );

// tslint:disable-next-line: cyclomatic-complexity
const BillingPageCard = (props: Props) => {
  const {
    page,
    promotions,
    accountDetails,
    openRemoveFromTeamModal,
    isCompanyAdmin,
    planTab,
    monthlyPlansAllotments,
    annualPlansAllotments,
    loadPricesForNextLevel,
    openBillingModal,
  } = props;
  const { outgoingMessagesLimit, downgradePlansIds, upgradePlansIds } = planTab;
  const ref = useRef<HTMLDivElement>(null);
  const billingPlan = getBillingPlanForTab(planTab);
  const dispatch = useDispatch();
  const modalActions = useModalActions();

  const currentSubscription = useSelector(getCurrentSubscription(page?.id));

  const plansAllotments =
    currentSubscription?.billingPeriodLength === 'annually'
      ? annualPlansAllotments
      : monthlyPlansAllotments;

  const enhancedPlans = useSelector(enhancedPlansSelector);

  const twilioNumbers = useSelector(twilioNumbersSelector);

  const isFreeTrialPromotion = !!page?.redemption?.freeTrial;
  const isInstachampPromotion = !!page?.redemption?.instachamp;
  const changeSeats = () => {
    if (!page.subscription && !currentSubscription) {
      return;
    }
    const seatsObject = {
      userSeats: currentSubscription?.userSeats || 0,
      extraUserSeats: currentSubscription?.extraUserSeats || 0,
    };
    if (planTab.plan) {
      openBillingModal({
        facebookPage: page,
        plan: planTab.plan,
        seatsObject,

        subscription:
          (page.subscription && currentSubscription) || page.subscription,

        onSuccessBilling: () =>
          modalActions.openGeneralTextModal({
            header: 'User seats changed',
            message: `All set! Available seats have been updated on your account. You can invite users to fill these seats on the User dashboard`,
          }),
      });
    }
  };

  const planInfoMemoised = useMemo(() => {
    return getPlanInfo(page, billingPlan);
  }, [page, billingPlan]);

  const plan: EnhancedPlan[] = [];

  if (planTab.plan) {
    plan.push(planTab.plan);
  }

  const plansAllotmentsOptions = mapPlanAllotmentOptions({
    plansAllotments,
    getExtraAllotment: false,
    page,
    plan,
    useFiltered: false,
  });
  const extraPlansAllotmentsOptions = mapPlanAllotmentOptions({
    plansAllotments,
    getExtraAllotment: true,
    page,
    plan,
    useFiltered: false,
  });
  const [selectedLevel, setSelectedLevel] = useState(plansAllotmentsOptions[0]);
  const [selectedLevelChanged, setSelectedLevelChanged] = useState(false);

  const { isFreeTrialPromotionWithFutureSubscription } =
    useFreeTrialUpgradeModal();

  const seatsMap = useSelector(getPlansUserSeats);
  const { messagesRemaining } = page.messagesStatistic;
  const showPromoRedeemSelector =
    !!planInfoMemoised.isFreePlan && !!promotions.length && isCompanyAdmin;

  const subscriptionType = isFreeTrialPromotionWithFutureSubscription
    ? 'Monthly'
    : capitalize(currentSubscription?.billingPeriodLength) || '';

  const subscriptionStatus = isFreeTrialPromotionWithFutureSubscription
    ? 'Future'
    : capitalize(currentSubscription?.state) || '';

  const nextBillingAmount = currentSubscription?.expiresAt
    ? 'n/a'
    : `$${centsToDollars(currentSubscription?.nextInvoiceAmount?.sum || 0)}`;

  const nextBillPlanName = enhancedPlans.find(
    p => p.planCode === currentSubscription?.planCode,
  )?.shortName;

  const subscriptionPrice = currentSubscription?.nextInvoiceAmount?.subsPrice
    ? ` ($${centsToDollars(currentSubscription?.nextInvoiceAmount?.subsPrice)})`
    : '';

  const nextBillSubscription = nextBillPlanName
    ? `   ${nextBillPlanName} subscription${subscriptionPrice}`
    : '';

  const seatsPrice = currentSubscription?.nextInvoiceAmount?.addOns?.userSeats
    ?.priceTotal
    ? ` ($${centsToDollars(
        currentSubscription?.nextInvoiceAmount?.addOns.userSeats.priceTotal,
      )})`
    : '';

  const nextBillExtraSeats = seatsPrice
    ? ` + ${currentSubscription?.nextInvoiceAmount?.addOns?.userSeats?.quantity} Extra user seats${seatsPrice}`
    : '';

  const sendsPrice = currentSubscription?.nextInvoiceAmount?.addOns
    ?.sendAllotment?.priceTotal
    ? ` ($${centsToDollars(
        currentSubscription?.nextInvoiceAmount?.addOns.sendAllotment.priceTotal,
      )})`
    : '';

  const period =
    currentSubscription?.billingPeriodLength === 'annually' ? 'year' : 'month';

  const sendCreditsInfo =
    page?.messagesStatistic &&
    getUsedAndTotal(page.messagesStatistic, currentSubscription);

  const thisPeriodCredits = abbreviateNumber(
    plansAllotmentsOptions.find(
      o =>
        o.level ===
        currentSubscription?.currentInvoiceAmount?.addOns?.sendAllotment
          ?.quantity,
    )?.name ||
      sendCreditsInfo?.[1] ||
      '',
  );

  const nextPeriodExtraCredits = abbreviateNumber(
    extraPlansAllotmentsOptions.find(
      o =>
        o.level ===
        currentSubscription?.nextInvoiceAmount?.addOns?.sendAllotment?.quantity,
    )?.name || '',
  );

  const nextBillExtraSends = sendsPrice
    ? ` + ${nextPeriodExtraCredits} extra sends${sendsPrice}`
    : '';

  const nextPeriodTotalCredits = abbreviateNumber(
    ['canceled', 'expired'].find(s => s === currentSubscription?.state)
      ? 1000
      : plansAllotmentsOptions.find(
          o =>
            o.level ===
            currentSubscription?.nextInvoiceAmount?.addOns?.sendAllotment
              ?.quantity,
        )?.name || '',
  );

  const additionalPagesPrice = currentSubscription?.nextInvoiceAmount?.addOns
    ?.additionalPages?.priceTotal
    ? ` ($${centsToDollars(
        currentSubscription?.nextInvoiceAmount?.addOns.additionalPages
          .priceTotal,
      )})`
    : '';

  const nextBillAdditionalPages = additionalPagesPrice
    ? ` + ${currentSubscription?.nextInvoiceAmount?.addOns?.additionalPages?.quantity} Additional pages${additionalPagesPrice}`
    : '';

  const smsNumberPrice = currentSubscription?.nextInvoiceAmount?.addOns
    ?.smsNumber?.priceTotal
    ? ` ($${centsToDollars(
        currentSubscription?.nextInvoiceAmount?.addOns?.smsNumber.priceTotal,
      )})`
    : '';

  const nextBillSmsNumber = smsNumberPrice
    ? ` + ${currentSubscription?.nextInvoiceAmount?.addOns?.smsNumber?.quantity} SMS numbers${smsNumberPrice}`
    : '';

  const nextBillCombinedInfo =
    nextBillingAmount +
    nextBillSubscription +
    nextBillExtraSeats +
    nextBillAdditionalPages +
    nextBillSmsNumber +
    nextBillExtraSends;

  const lastCardNumbers = getLastCardNumbers(accountDetails);
  const billedWith = `XXXX-XXXX-XXXX-${lastCardNumbers}`;

  const allotedCredits = (
    <div className="billing-page-card__plan-info--period-details">
      {thisPeriodCredits && (
        <div className="billing-page-card__plan-info--period-details--left">
          This {period}: {thisPeriodCredits}
        </div>
      )}
      {nextPeriodTotalCredits && (
        <div className="billing-page-card__plan-info--period-details--right">
          Next {period}: {nextPeriodTotalCredits}
        </div>
      )}
    </div>
  );

  const thisPeriodSeats =
    currentSubscription?.currentInvoiceAmount?.addOns?.userSeats?.quantity;
  const nextPeriodSeats =
    currentSubscription?.nextInvoiceAmount?.addOns?.userSeats?.quantity;

  const additionalSeats = (
    <div className="billing-page-card__plan-info--period-details">
      {(thisPeriodSeats || thisPeriodSeats === 0) && (
        <div className="billing-page-card__plan-info--period-details--left">
          This {period}: {thisPeriodSeats}
        </div>
      )}
      {(nextPeriodSeats || nextPeriodSeats === 0) && (
        <div className="billing-page-card__plan-info--period-details--right">
          Next {period}: {nextPeriodSeats}
        </div>
      )}
    </div>
  );
  const subscriptionIsCanceled = getSubscriptionIsCanceled(page);

  const subscriptionEndsAt =
    subscriptionIsCanceled && page.subscription
      ? moment(page.subscription.endsAt).format('MM/DD/YYYY')
      : 'n/a';

  const nextBillingDate =
    page.subscription && !subscriptionIsCanceled && subscriptionEndsAt === 'n/a'
      ? moment(page.subscription.endsAt).format('MM/DD/YYYY')
      : 'n/a';

  const overagesNotShowingInSendLimit = page.overagesEnabled
    ? true
    : !!messagesRemaining;

  const customOptionRenderer = (option: DropdownOption) => {
    const { subscription } = page || {};
    const { price, formattedName } = getFormatedPriceAndName(option);

    const period =
      subscription?.billingPeriodLength === 'annually' ? 'year' : 'month';

    return (
      <span>
        {subscription && (
          <span className="billing-page-card__select--number">
            <strong>${price}</strong>
          </span>
        )}
        {subscription && ' '}
        <span className="billing-page-card__select">
          {formattedName} credits/{period}{' '}
        </span>
      </span>
    );
  };

  const getFormatedPriceAndName = (option: DropdownOption) => {
    const { subscription } = page || {};
    const { level, name } = option;
    const price = level * (subscription?.sendAllotmentAddOnPrice || 0);
    const formattedName = name?.replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1,');
    return { price, formattedName };
  };

  const onDropdownItemChange = (plan: {
    id: number;
    name: string;
    level: number;
  }) => {
    setSelectedLevel(plan);
    setSelectedLevelChanged(true);
  };

  const onOpenConfirmSendsModal = () => {
    const { subscription } = page || {};
    if (!subscription) {
      return;
    }
    const { price, formattedName } = getFormatedPriceAndName(selectedLevel);

    loadPricesForNextLevel(subscription.id, selectedLevel.level);

    const { sendAllotmentLevel } = subscription;

    const modalText =
      selectedLevel.level > sendAllotmentLevel
        ? `You've successfully added additional credits to your subscription. 
        The total sends allotment is ${formattedName} credits ${currentSubscription?.billingPeriodLength}. 
        You'll be charged ${currentSubscription?.billingPeriodLength} $${price} extra.`
        : `You've successfully downgraded to a lower send tier. You will be charged a lower amount on your next bill`;

    openBillingModal({
      level: selectedLevel,
      subscription: currentSubscription || subscription,
      facebookPage: page,
      plan: planTab.plan,
      onSuccessBilling: () =>
        modalActions.openGeneralTextModal({
          header: 'Credits changed',
          message: modalText,
        }),
    });
    setSelectedLevelChanged(false);
  };

  // tslint:disable-next-line: cyclomatic-complexity
  const mapPlanSeatsAmount = () => {
    const { isProPlan, isProUnicorn, isAnyTeamPlan } = planInfoMemoised;
    if (!page || !page.plan) {
      return 0;
    }

    return isProPlan
      ? seatsMap.pro
      : isProUnicorn
      ? seatsMap.unicorn
      : isAnyTeamPlan
      ? seatsMap.team
      : 0;
  };

  useEffect(() => {
    if (page.subscription) {
      const { sendAllotmentLevel } = page.subscription;

      const planAllotmentCurrent = plansAllotmentsOptions.find(allotment => {
        return Number(allotment.level) === sendAllotmentLevel;
      });
      if (sendAllotmentLevel && planAllotmentCurrent) {
        setSelectedLevel(planAllotmentCurrent);
      } else {
        setSelectedLevel(plansAllotmentsOptions[0]);
      }
    }
  }, [planInfoMemoised, JSON.stringify(plansAllotmentsOptions)]);

  const LevelChoosesTemplate = () => {
    const { isProUnicorn, isProPlan, isAnyTeamPlan, isFreePlan, isInstachamp } =
      planInfoMemoised;
    const isOneOfSupportedPlans =
      isProUnicorn || isProPlan || isAnyTeamPlan || isInstachamp;
    const showPopup = isNumber(
      page.subscription?.addOnDowngrades.allotmentLevelDowngrade,
    );
    if (
      isFreePlan ||
      !plansAllotmentsOptions.length ||
      !isOneOfSupportedPlans ||
      !accountDetails?.subscriptions?.length ||
      !page.subscription?.endsAt ||
      subscriptionEndsAt !== 'n/a'
    ) {
      return null;
    }
    const allotmentLevelDowngrade = page.subscription?.addOnDowngrades
      .allotmentLevelDowngrade
      ? page.subscription?.addOnDowngrades.allotmentLevelDowngrade
      : 0;

    const loweredSendsAmount = isNumber(allotmentLevelDowngrade)
      ? plansAllotmentsOptions.find(
          option => option.level === allotmentLevelDowngrade,
        )?.name
      : 0;

    return (
      <div>
        <div className="billing-page-card__extra-sends">
          Total{' '}
          {currentSubscription?.billingPeriodLength === 'annually'
            ? 'Annual'
            : 'Monthly'}{' '}
          Sends
        </div>
        <div className="active-plan-details__select-wrapper">
          <PrimarySelect
            showSearchFilter={false}
            className="active-plan-details__select-card"
            placeholder="Choose a plan"
            onChange={(plan: { id: number; name: string; level: any }) =>
              onDropdownItemChange(plan)
            }
            disabled={showPopup}
            options={plansAllotmentsOptions}
            value={selectedLevel}
            customOptionRenderer={customOptionRenderer}
          />
          {showPopup && (
            <PrimaryToolTip
              useHover={true}
              classNameContent="active-plan-details__tooltip-padding"
              anchorElement={<i className="far fa-question-circle" />}
            >
              Lowered since next billing cycle to{' '}
              {abbreviateNumber(loweredSendsAmount || 0)} credits
            </PrimaryToolTip>
          )}
          {selectedLevelChanged && (
            <PrimaryButton
              onClick={onOpenConfirmSendsModal}
              className="active-plan-details__action"
            >
              Change Limits
            </PrimaryButton>
          )}
        </div>
      </div>
    );
  };

  const isSubscriptionBusy = useSelector(
    (state: StoreState) => state.subscription.busy,
  );

  const [currentSubscriptionId, setCurrentSubscriptionId] = useState(0);

  useEffect(() => {
    if (
      page.subscription &&
      !currentSubscription &&
      !planInfoMemoised.isAnyTeamPlan &&
      !isSubscriptionBusy
    ) {
      if (page.subscription.id === currentSubscriptionId) {
        return;
      }
      dispatch(subscriptionCRUDActions.getCurrentSubscription(page.id, false));
      setCurrentSubscriptionId(page.subscription.id);
    }
  }, [
    currentSubscriptionId,
    currentSubscription,
    dispatch,
    isSubscriptionBusy,
    page.subscription,
    planInfoMemoised.isAnyTeamPlan,
  ]);

  const { openConditionalDowngradeModal } = useOpenConditionalDowngradeModal();

  const showSubscr =
    page.subscription &&
    currentSubscription?.nextInvoiceAmount &&
    currentSubscription?.nextInvoiceAmount.sum;

  const showFieldNextBillAmount = Boolean(
    showSubscr && page.subscription && currentSubscription,
  );

  const customTeamPlanConditions =
    (planInfoMemoised.isLegacyTeamPlan || planInfoMemoised.isCustomTeam) &&
    !!twilioNumbers.length;

  const showSendCreditsUsed =
    (planInfoMemoised.isAnyTeamPlan && outgoingMessagesLimit) ||
    customTeamPlanConditions;

  const showSendCreditsAllotmentsAndSeats = !(
    planInfoMemoised.isLegacyTeamPlan ||
    planInfoMemoised.isCustomTeam ||
    planInfoMemoised.isSpecialTeam ||
    planInfoMemoised.isLegacyProPlan
  );

  const isNonStandardPlan =
    planInfoMemoised.isAnyTeamPlan ||
    planInfoMemoised.isPromoPlan ||
    planInfoMemoised.isLegacyProPlan ||
    planInfoMemoised.isLegacyTeamPlan;

  const billingPageCardMainPartClassNames = classNames(
    'billing-page-card__main-part',
    { 'billing-page-card__main-part--white-bg': isNonStandardPlan },
  );

  return (
    <div className="billing-page-card" ref={ref}>
      <ImageWithFallback
        src={page.squarePicture || noImage}
        fallbackImage={noImage}
        className="billing-page-card__page-image"
        alt=""
      />
      <div className="billing-page-card__top">
        <div>
          <div className="billing-page-card__meta-data-top">
            <div className="billing-page-card__name">{page.name}</div>

            <PlanBadge billingType={billingPlan || page.pageBillingType} />

            {!subscriptionIsCanceled &&
              !isFreeTrialPromotion &&
              page.subscription && (
                <ThreeDotMenu
                  className="billing-page-card__dot-menu"
                  contentClassName="billing-page-card__dot-menu--item"
                  onClick={e => e.stopPropagation()}
                  elements={[{ name: 'Cancel', value: 'cancel' }]}
                  selectItem={menuValue => {
                    if (menuValue === 'cancel' && page.subscription) {
                      openConditionalDowngradeModal({
                        subscription: page.subscription,
                        plan: planTab.plan!,
                        facebookPage: page,
                        tabId: planTab.id,
                      });
                    }
                  }}
                />
              )}
          </div>

          <div className="billing-page-card__connected-since">
            Connected Since: {moment(page.createdAt).format('MM/DD/YYYY')}
          </div>
        </div>

        <div className="billing-page-card__meta-data">
          {(planInfoMemoised.isAnyTeamPlan || !page.subscription) && (
            <a
              className="facebook-page-card__facebook-link"
              href={`https://facebook.com/${page.remoteId}`}
              target="_blank"
              rel="noopener noreferrer"
            >
              Facebook Page Id: {page.remoteId}
            </a>
          )}

          {showSendCreditsUsed && (
            <div className="billing-page-card__team-sends">
              <div className="billing-page-card__team-sends-text">
                Credits used
              </div>
              <div className="billing-page-card__team-sends-number">
                {customTeamPlanConditions
                  ? page?.currentOutgoingMessagesStatistic?.count
                  : page.messagesStatistic.pageSpecificCount}
              </div>
            </div>
          )}

          {!planInfoMemoised.isAnyTeamPlan && page.subscription && (
            <div className="billing-page-card__plan-info">
              <div className="billing-page-card__plan-info--column_one">
                <SubscriptionMetaField
                  label="Facebook page ID:"
                  value={
                    <a
                      className="facebook-page-card__facebook-link"
                      href={`https://facebook.com/${page.remoteId}`}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {page.remoteId}
                    </a>
                  }
                />
                <SubscriptionMetaField
                  label="Subscription Type:"
                  value={subscriptionType}
                />
                <SubscriptionMetaField
                  label="Subscription Status:"
                  value={subscriptionStatus}
                />
                <SubscriptionMetaField
                  label="Plan expires:"
                  value={subscriptionEndsAt}
                />
                <SubscriptionMetaField
                  label="Next Billing Date:"
                  value={nextBillingDate}
                />
              </div>
              <div className="billing-page-card__plan-info--column_two">
                {showFieldNextBillAmount && (
                  <SubscriptionMetaField
                    label="NEXT BILL AMOUNT:"
                    value={nextBillCombinedInfo}
                  />
                )}
                <SubscriptionMetaField
                  label="Billed With:"
                  value={billedWith}
                />
                {showSendCreditsAllotmentsAndSeats && (
                  <SubscriptionMetaField
                    label="Total Allotted Credits:"
                    value={allotedCredits}
                  />
                )}
                {showSendCreditsAllotmentsAndSeats &&
                  !!(thisPeriodSeats || nextPeriodSeats) && (
                    <SubscriptionMetaField
                      label="Additional Seats:"
                      value={additionalSeats}
                    />
                  )}
              </div>
            </div>
          )}
        </div>
        <div className={billingPageCardMainPartClassNames}>
          <div className="billing-page-card__left-section">
            <div className="billing-page-card__left-section-left">
              {!planInfoMemoised.isAnyTeamPlan &&
                (outgoingMessagesLimit ||
                  isFreeTrialPromotion ||
                  isInstachampPromotion) && (
                  <div className="billing-page-card__percentage-bar-wrapper">
                    <UsagePercentageBar
                      className="billing-page-card__percentage-bar"
                      fbPage={page}
                    />
                    {page.messagesStatistic.utilizationPercentage > 79 &&
                      page.messagesStatistic.utilizationPercentage < 100 &&
                      !page.overagesEnabled &&
                      !isFreeTrialPromotion && (
                        <div className="billing-page-card__exclamation-icon-container">
                          <ExclamationIcon className="billing-page-card__exclamation-icon" />
                          <span>
                            Approaching Send Credit limit. Upgrade now or check
                            off the option below to avoid an interruption in
                            service.
                          </span>
                        </div>
                      )}
                  </div>
                )}
            </div>
          </div>
          <div className="billing-page-card__left-section-right">
            {isCompanyAdmin && <PromoCodeArea page={page} />}
          </div>

          <div className="billing-page-card__right-section-wrapper">
            {page &&
              page.subscription &&
              currentSubscription &&
              !(
                planInfoMemoised.isFreePlan ||
                planInfoMemoised.isInstachamp ||
                isNonStandardPlan
              ) && (
                <SeatsArea
                  changeSeats={changeSeats}
                  userSeatsInPlan={mapPlanSeatsAmount()}
                  userSeats={currentSubscription?.userSeats}
                  extraUserSeats={currentSubscription?.extraUserSeats}
                  userSeatsUsed={currentSubscription?.userSeatsUsed}
                  userSeatsDowngrade={
                    currentSubscription?.addOnDowngrades?.userSeatsDowngrade
                  }
                  disabled={
                    !!page.subscription?.expiresAt &&
                    page.subscription?.state !== 'active'
                  }
                />
              )}
            <BillingCardRightSection
              outgoingMessagesLimit={outgoingMessagesLimit}
              messagesRemaining={messagesRemaining}
              showPromotionsSelector={showPromoRedeemSelector}
              promotions={promotions}
              subscriptionIsCanceled={subscriptionIsCanceled}
              page={page}
              subscriptionEndsAt={subscriptionEndsAt}
              openRemoveFromTeamModal={openRemoveFromTeamModal}
              upgradePlansIds={upgradePlansIds}
              downgradePlansIds={downgradePlansIds}
              planInfo={planInfoMemoised}
              plan={planTab.plan}
            />
          </div>
        </div>

        {!isFreeTrialPromotion && !isNonStandardPlan && (
          <LevelChoosesTemplate />
        )}
        {planCanManageOverages(outgoingMessagesLimit, planInfoMemoised) &&
          overagesNotShowingInSendLimit &&
          !(planInfoMemoised.isSpecialPlan && !planInfoMemoised.isInstachamp) &&
          !(isFreeTrialPromotion && !page.hardLimitReached) &&
          subscriptionEndsAt === 'n/a' && (
            <div className="billing-page-card__overages">
              <OveragesManagement facebookPage={page} showLink={false}>
                <a
                  className="send-limit-alert-message__overage-management-link"
                  href="https://mobilemonkey.com/help/article/03nc6a7axs-send-overage-automatic-upgrades"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  View Pricing
                </a>
              </OveragesManagement>
            </div>
          )}
      </div>
    </div>
  );
};

export default BillingPageCard;
